import { useState, useEffect } from 'react';
import { useAuthStore } from '@/common/stores';

/**
 * Validates the persisted access token on app mount.
 * Always starts as false (not initialized) so the loading screen shows until
 * the auth check completes, regardless of persist-rehydration timing.
 * Returns true once the check is done and routing can begin.
 */
export function useAuthInit(): boolean {
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const getCurrentUser = useAuthStore((s) => s.getCurrentUser);
  const clearAuthState = useAuthStore((s) => s.clearAuthState);

  // Always start uninitialized so the LoadingScreen gates routing until we know
  // the real auth state — avoids blank-page flashes when persist rehydrates async.
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    // No credentials stored — nothing to validate, proceed immediately.
    if (!accessToken && !isAuthenticated) {
      setInitialized(true);
      return;
    }

    // Has stored credentials — validate against the server before routing.
    getCurrentUser()
      .catch(() => clearAuthState())
      .finally(() => setInitialized(true));
    // Run once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return initialized;
}
